import { useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import CourseAssignmentModal from './CourseAssignmentModal';
import Button from '../../../common/Button/Button';
import Layout from '../../../common/Layout/Layout';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../../base/Notification/Notification';
import { pageTypeToCourseTypeMap, useViewContext } from '../../../../lib/context/ViewContext';
import { AssignmentCommonTableProps, CourseAssignmentProps } from './CourseAssignment.types';
import CourseIndividuals from '../../base/CourseIndividuals/CourseIndividuals';
import CourseTeams from '../../base/CourseTeams/CourseTeams';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import styles from './CourseAssignment.module.scss';
import useDatePicker from '../../../../lib/hooks/useDatePicker';
import { Label } from '../../../common/Label/Label';
import { ASSIGN_COURSE_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { useLabel } from '../../../../lib/hooks/labels';
import StandaloneTabs from '../Tabs/StandaloneTabs';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import useDeleteState from '../../../../lib/hooks/useDeleteState';
import LazyDateRangePicker from '../../base/DateRangePicker/LazyDateRangePicker';
import { Api } from '../../../../lib/types/apiSpec';
import { PageType } from '../../../../lib/hooks/usePageDetails';

const CourseAssignment = ({
  successMessage,
  buttonLabel,
  pageSize,
  pageSizeOptions,
  showPageSizeSelector,
  headingLevel,
  heading,
  icon,
  removeUserSuccessMessage,
  removeTeamSuccessMessage,
  teamReportConfig,
  teamReportDialogTitle,
  ...rest
}: CourseAssignmentProps) => {
  const [showModal, setShowModal] = useState(false);
  const { mutate } = useSWRConfig();
  const picker = useDatePicker();
  const { page } = useViewContext();
  const teamsLabel = useLabel(COMMON_LABELS.TAB.TEAMS);
  const individualsLabel = useLabel(COMMON_LABELS.TAB.USERS);
  const courseData = page.type === PageType.LearningPathDetails ? page.learningPath : page.course;
  const courseType = pageTypeToCourseTypeMap[page.type];
  const { triggerSnackbar } = useSnackbarContext();
  const { id: courseId } = courseData ?? {};
  const [teamsDeleteState, teamsDeleteStateDispatch] = useDeleteState({});
  const [individualsDeleteState, individualsDeleteStateDispatch] = useDeleteState({});

  const handleSuccess = (fullSuccess: boolean = true) => {
    mutate(([key]: string[]) =>
      [`/${courseId}/teams/summary`, `/${courseId}/users`]
        .map(url => `/${courseType === Api.TrainingType.Course ? 'courses' : 'learningpaths'}${url}`)
        .some(url => key.includes(url)),
    );

    if (fullSuccess) {
      triggerSnackbar(successMessage, NotificationType.Success);
      setShowModal(false);
    }
  };

  const range: AssignmentCommonTableProps['range'] = useMemo(() => {
    if (!!picker.startDate && !!picker.endDate) {
      return { since: picker.startDate, to: picker.endDate };
    }

    return undefined;
  }, [picker.startDate, picker.endDate]);

  const handleCancel = () => {
    setShowModal(false);
  };

  if (page.error) {
    return <ErrorInfo error={page.error} />;
  }

  if (!courseData || !courseType || !courseData.id) {
    return null;
  }

  const tablesProps: AssignmentCommonTableProps = {
    courseDetails: courseData,
    type: courseType,
    pageSize,
    showPageSizeSelector,
    pageSizeOptions,
    range,
  };

  return (
    <Layout className={styles.wrapper}>
      <HeadingWithIcon level={HeadingLevel.h2} as={headingLevel ?? HeadingLevel.h3} icon={icon}>
        {heading}
      </HeadingWithIcon>
      <div className={styles.detailsRow}>
        <Label name={ASSIGN_COURSE_LABELS.DATE_ASSIGNED} className={styles.dateLabel} />
        <LazyDateRangePicker {...picker} buttonClasses={styles.dateRangePicker} />
        <Button onClick={() => setShowModal(true)} className={styles.assignButton}>
          {buttonLabel}
        </Button>
      </div>
      <CourseAssignmentModal
        {...rest}
        isModalOpened={showModal}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        courseDetails={courseData}
        type={courseType}
      />
      <StandaloneTabs
        elementsClasses={{ tabNavigation: styles.tabs, activeTab: styles.activeTab, tabContainer: styles.tabContainer }}
        cqItems={{
          [teamsLabel]: { 'cq:panelTitle': teamsLabel },
          [individualsLabel]: { 'cq:panelTitle': individualsLabel },
        }}
        customPanelTitles={{
          [teamsLabel]: <Label name={COMMON_LABELS.TAB.TEAMS} />,
          [individualsLabel]: <Label name={COMMON_LABELS.TAB.USERS} />,
        }}
        cqItemsOrder={[teamsLabel, individualsLabel]}
        updateHash={false}
        items={{
          [teamsLabel]: {
            top: (
              <CourseTeams
                removeTeamSuccessMessage={removeTeamSuccessMessage}
                itemsDeleteState={teamsDeleteState}
                itemsDeleteStateDispatch={teamsDeleteStateDispatch}
                teamReportConfig={teamReportConfig}
                teamReportDialogTitle={teamReportDialogTitle}
                {...tablesProps}
              />
            ),
          },
          [individualsLabel]: {
            top: (
              <CourseIndividuals
                removeUserSuccessMessage={removeUserSuccessMessage}
                itemsDeleteState={individualsDeleteState}
                itemsDeleteStateDispatch={individualsDeleteStateDispatch}
                {...tablesProps}
              />
            ),
          },
        }}
      />
    </Layout>
  );
};

export default CourseAssignment;
